import * as React from "react";
import {
  ActionCode,
  AgentFunctionsEditDto,
  EFunctionRequirement,
} from "../../../api";
import { CardFieldGroup, CustomFormGenerator } from "../../../components";
import { useCommonHooks } from "../../../hooks";
import { FlexFieldGroup, showSuccess, useFGFormik } from "nsitools-react";
import { useCrudApi } from "../../../hooks/useCrudApi";
import * as Yup from "yup";
import AgentRequiredFunctionGrid from "./AgentRequiredFunctionGrid";

export interface AgentFunctionGridProps {
  agentId: number;
  readonly: boolean;
}

export const AgentFunctionGrid: React.FunctionComponent<
  AgentFunctionGridProps
> = ({ agentId, readonly }) => {
  const { apis, t, hasAction, user } = useCommonHooks();
  const isEditable = React.useMemo(
    () => hasAction([ActionCode.EditAgent]) && agentId !== user.agentId,
    [agentId, hasAction, user.agentId]
  );

  const { data, isFetching, validationErrors, onSave, isSaving, refetch } =
    useCrudApi({
      queryKey: ["agent-functions", agentId],
      fetchFn: async () => {
        if (agentId > 0) {
          return apis.agentFunctions.getAgentFunctions(agentId);
        }

        return AgentFunctionsEditDto.fromJS({});
      },
      saveFn: async (toSave: AgentFunctionsEditDto) => {
        return apis.agentFunctions.saveAgentFunctions(toSave);
      },
      onSaved: () => {
        showSuccess(t("AgentSaved"));
        refetch();
      },
      serverValidationRootKey: "Dto",
    });

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        agentFunctions: Yup.array().of(
          Yup.object().shape({
            specialities: Yup.array().of(
              Yup.object().shape({
                functionalDirectorId: Yup.number()
                  .nullable()
                  .when(["isChecked", "hasDirection"], {
                    is: (isChecked, hasDirection) => isChecked && hasDirection,
                    then: (schema) => schema.required(t("Required")),
                    otherwise: (schema) => schema,
                  }),
                positionId: Yup.number()
                  .nullable()
                  .when(["isChecked", "hasPosition"], {
                    is: (isChecked, hasPosition) => isChecked && hasPosition,
                    then: (schema) => schema.required(t("Required")),
                    otherwise: (schema) => schema,
                  }),
              })
            ),
          })
        ),
      }),
    [t]
  );

  const formik = useFGFormik({
    initialValues: data,
    onSubmit: (d) => onSave(d),
    validationSchema: validationSchema,
  });

  const getFunctionIndexById = React.useCallback(
    (id: number) => {
      return formik.values?.agentFunctions.findIndex((r) => r.id === id);
    },
    [formik.values?.agentFunctions]
  );

  const getSpecialityIndexById = React.useCallback(
    (functionId: number, id: number) => {
      return formik.values?.agentFunctions[
        getFunctionIndexById(functionId)
      ].specialities.findIndex((r) => r.id === id);
    },
    [formik.values?.agentFunctions, getFunctionIndexById]
  );

  React.useEffect(() => {
    const agentFunctionsToUpdate = formik?.values?.agentFunctions
      ?.flatMap((a) => a.specialities)
      .filter(
        (spe) =>
          !spe.isChecked &&
          (spe.functionalDirectorId !== null || spe.positionId != null)
      );

    if (agentFunctionsToUpdate?.length > 0) {
      agentFunctionsToUpdate.forEach((item) => {
        const valueName = `agentFunctions[${getFunctionIndexById(
          item.agentFunctionId
        )}].specialities[${getSpecialityIndexById(
          item.agentFunctionId,
          item.id
        )}].isChecked`;

        formik.setFieldValue(valueName, true);
      });
    }
  }, [formik, getFunctionIndexById, getSpecialityIndexById]);

  return (
    <FlexFieldGroup>
      <CardFieldGroup title={t("Functions")}>
        <CustomFormGenerator
          initialValues={data}
          loading={isFetching}
          onSubmit={onSave}
          saving={isSaving}
          editable={isEditable}
          editMode={isEditable}
          showDeleteButton={false}
          validationErrors={validationErrors}
          validationSchema={validationSchema}
          formik={formik}
        >
          <AgentRequiredFunctionGrid
            loading={false}
            requirement={EFunctionRequirement.Required}
            readonly={readonly}
          />
          <AgentRequiredFunctionGrid
            loading={false}
            requirement={EFunctionRequirement.Optional}
            readonly={readonly}
          />
        </CustomFormGenerator>
      </CardFieldGroup>
    </FlexFieldGroup>
  );
};

export default AgentFunctionGrid;
